import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, ErroHttp } from '@/lib/api';
import type {
  RelatorioEstoqueAtual,
  RelatorioLocalizadores,
  SituacaoSaldo,
} from '@/lib/tipos';

function tentarDeNovo(tentativas: number, erro: unknown): boolean {
  if (erro instanceof ErroHttp && (erro.naoAutenticado || erro.semPermissao || erro.status === 400)) {
    return false;
  }
  return tentativas < 2;
}

export interface FiltrosLocalizadores {
  corredor: string;
  modulo: string;
  status: string;
  q: string;
}

/**
 * Cadastro de localizadores com ocupação. O corte por rua/módulo vai para o
 * servidor: o CD 2201 tem 12 mil posições e mandar tudo para filtrar no
 * navegador a cada tecla seria a morosidade que o projeto quer eliminar.
 */
export function useRelatorioLocalizadores(
  centro: number | null,
  filtros: FiltrosLocalizadores,
): UseQueryResult<RelatorioLocalizadores, unknown> {
  return useQuery<RelatorioLocalizadores>({
    queryKey: ['rel-localizadores', centro, filtros],
    queryFn: () =>
      api.get<RelatorioLocalizadores>('/api/relatorios/localizadores', {
        centro,
        corredor: filtros.corredor || undefined,
        modulo: filtros.modulo || undefined,
        status: filtros.status || undefined,
        q: filtros.q || undefined,
      }),
    enabled: centro !== null,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}

export interface FiltrosEstoqueAtual {
  produto: string;
  corredor: string;
  modulo: string;
  situacao: SituacaoSaldo;
  q: string;
  limite: number;
}

export function useRelatorioEstoque(
  centro: number | null,
  filtros: FiltrosEstoqueAtual,
): UseQueryResult<RelatorioEstoqueAtual, unknown> {
  return useQuery<RelatorioEstoqueAtual>({
    queryKey: ['rel-estoque', centro, filtros],
    queryFn: () =>
      api.get<RelatorioEstoqueAtual>('/api/relatorios/estoque', {
        centro,
        produto: filtros.produto || undefined,
        corredor: filtros.corredor || undefined,
        modulo: filtros.modulo || undefined,
        situacao: filtros.situacao,
        q: filtros.q || undefined,
        limite: filtros.limite,
      }),
    enabled: centro !== null,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}
